import { ComponentPropsWithoutRef, MouseEvent, Ref, forwardRef } from "react";
import { useTrackingId } from "../../hooks";
import {
  DataTrackingId,
  LayoutUtilProps,
  OnCloseProps,
  Status,
  Svg,
} from "../../types";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import Info from "@servicetitan/hammer-icon/mdi/round/info.svg";
import Warning from "@servicetitan/hammer-icon/mdi/round/warning.svg";
import Danger from "@servicetitan/hammer-icon/mdi/round/error.svg";
import Success from "@servicetitan/hammer-icon/mdi/round/check_circle.svg";
import Icon from "../Icon";
import Button from "../Button";
import { Text } from "../Text";
import { AiMarkWithTooltipOrPopoverConfig } from "../../types/ai-marks";

import { useLayoutPropsUtil } from "../../internal/hooks";

import cx from "classnames";
import styles from "./Alert.module.scss";

/**
 * Props for the Alert component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 * @extends OnCloseProps
 */
export type AlertProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps &
  OnCloseProps & {
    /**
     * The visual style of an alert.
     * @default info
     */
    status?: Status;
    /**
     * The title text displayed in the alert.
     */
    title: string;
    /**
     * When set, displays the AI mark inline after the title.
     * Use `true` for the sparkle only, or pass tooltip/popover configuration.
     */
    aiMark?: boolean | AiMarkWithTooltipOrPopoverConfig;
  } &
  /**
   * Data tracking id
   */
  DataTrackingId;

const statusIconMap: Record<NonNullable<AlertProps["status"]>, Svg> = {
  info: Info,
  success: Success,
  warning: Warning,
  danger: Danger,
};

/**
 * Alert component for displaying important messages to users.
 *
 * Features:
 * - Supports different visual styles (info, success, warning, danger)
 * - Optional close button for dismissible alerts
 * - Optional AI mark inline after the title
 * - Customizable title and content
 * - Accessible with proper ARIA roles
 * - Includes status-specific icons
 * - Supports layout utilities for positioning and spacing
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Alert
 *   title="System Update"
 *   onClose={() => console.log('Alert closed')}
 * >
 *   The system will be updated tonight.
 * </Alert>
 *
 * @example
 * <Alert
 *   status="success"
 *   title="Operation Complete"
 * >
 *   Your changes have been saved successfully.
 * </Alert>
 */
export const Alert = forwardRef(
  (props: AlertProps, ref: Ref<HTMLDivElement>) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      children,
      className,
      title,
      status = "info",
      style,
      onClose,
      aiMark,
      ...rest
    } = componentProps;

    const data = {
      title,
      status,
    };

    const trackingId = useTrackingId({
      name: "Alert",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const alertCx = cx(styles["alert"], className, {
      [styles["success"]]: status === "success",
      [styles["warning"]]: status === "warning",
      [styles["danger"]]: status === "danger",
    });

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <div
        data-tracking-id={trackingId}
        data-anv="alert"
        role="status"
        className={alertCx}
        style={styleCombined}
        {...rest}
        ref={ref}
      >
        <div className={styles["top"]}>
          <span className={styles["title-container"]}>
            <Icon
              size="large"
              className={styles["status-icon"]}
              svg={statusIconMap[status ?? "info"]}
            />
            <Text
              variant="headline"
              el="h6"
              size="small"
              className={styles["title"]}
              aiMark={aiMark || undefined}
            >
              {title}
            </Text>
          </span>
          {!!onClose && (
            <span className={styles["close-container"]}>
              <Button
                icon={Close}
                size="small"
                appearance="ghost"
                className={styles["close-button"]}
                onClick={(e: MouseEvent<HTMLButtonElement>) => onClose(e)}
                aria-label={`Dismiss ${status} alert`}
              />
            </span>
          )}
        </div>
        {children && <div className={styles["content"]}>{children}</div>}
      </div>
    );
  },
);

Alert.displayName = "Alert";
